import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Upload, Trash2, LogOut, Image as ImageIcon } from "lucide-react";
import { User, Session } from "@supabase/supabase-js";

interface ProductImage {
  id: string;
  category: string;
  product_slug: string;
  image_url: string;
  title: string | null;
  display_order: number;
}

const AdminDashboard = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<ProductImage[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedSlug, setSelectedSlug] = useState<string>("");
  const [imageTitle, setImageTitle] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { language, isRTL } = useLanguage();

  const categories = {
    aluminum: ["doors", "windows", "shades", "kitchens", "facades"],
    iron: ["hangars", "shades", "stairs", "railings"],
    glass: ["facades", "doors", "partitions", "tempered", "insulated", "decorative", "mirrors", "tables"],
    marble: ["kitchen-surfaces", "sinks", "counters", "tables", "decorations"],
    "stainless-steel": ["railings", "doors", "facades", "kitchens", "sinks", "decorations"],
    wood: ["home-furniture", "wooden-doors", "wooden-decorations", "restaurant-equipment", "office-furniture", "wall-cladding", "hotel-equipment", "furnished-apartments", "workspace-equipment", "wardrobes", "kitchen-cabinets"],
  };

  const categoryNames = {
    ar: {
      aluminum: "الألمنيوم",
      iron: "الحديد",
      glass: "الزجاج",
      marble: "الرخام",
      "stainless-steel": "الستانلس ستيل",
      wood: "الخشب",
    },
    en: {
      aluminum: "Aluminum",
      iron: "Iron",
      glass: "Glass",
      marble: "Marble",
      "stainless-steel": "Stainless Steel",
      wood: "Wood",
    },
  };

  const content = {
    ar: {
      title: "لوحة التحكم الإدارية",
      uploadImage: "رفع صورة جديدة",
      category: "الفئة",
      product: "المنتج",
      imageTitle: "عنوان الصورة (اختياري)",
      selectFile: "اختر ملف",
      upload: "رفع",
      existingImages: "الصور الحالية",
      noImages: "لا توجد صور",
      deleteConfirm: "هل أنت متأكد من حذف هذه الصورة؟",
      uploadSuccess: "تم رفع الصورة بنجاح",
      deleteSuccess: "تم حذف الصورة بنجاح",
      error: "حدث خطأ",
      logout: "تسجيل الخروج",
      notAdmin: "ليس لديك صلاحيات الإدارة",
      selectCategory: "اختر الفئة",
      selectProduct: "اختر المنتج",
      fileTooLarge: "حجم الملف كبير جداً. الحد الأقصى 5 ميجابايت",
      invalidFileType: "نوع الملف غير صالح. يُسمح فقط بـ JPEG, PNG, WebP, GIF",
      invalidExtension: "امتداد الملف غير صالح",
      notValidImage: "الملف ليس صورة صالحة",
      imageTooLarge: "أبعاد الصورة كبيرة جداً. الحد الأقصى 4096×4096",
    },
    en: {
      title: "Admin Dashboard",
      uploadImage: "Upload New Image",
      category: "Category",
      product: "Product",
      imageTitle: "Image Title (optional)",
      selectFile: "Select File",
      upload: "Upload",
      existingImages: "Existing Images",
      noImages: "No images",
      deleteConfirm: "Are you sure you want to delete this image?",
      uploadSuccess: "Image uploaded successfully",
      deleteSuccess: "Image deleted successfully",
      error: "An error occurred",
      logout: "Logout",
      notAdmin: "You don't have admin permissions",
      selectCategory: "Select Category",
      selectProduct: "Select Product",
      fileTooLarge: "File too large. Maximum 5MB allowed",
      invalidFileType: "Invalid file type. Only JPEG, PNG, WebP, GIF allowed",
      invalidExtension: "Invalid file extension",
      notValidImage: "File is not a valid image",
      imageTooLarge: "Image dimensions too large. Maximum 4096×4096",
    },
  };

  const t = content[language];
  const catNames = categoryNames[language];

  // Validate image file before upload
  const validateImageFile = async (file: File): Promise<{ valid: boolean; error?: string }> => {
    // 1. Size check (5MB limit)
    const MAX_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      return { valid: false, error: t.fileTooLarge };
    }

    // 2. MIME type check
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: t.invalidFileType };
    }

    // 3. Extension check
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
    const ext = file.name.toLowerCase().match(/\.[^.]*$/)?.[0];
    if (!ext || !allowedExtensions.includes(ext)) {
      return { valid: false, error: t.invalidExtension };
    }

    // 4. Verify it's actually an image and check dimensions
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        // 5. Check dimensions (max 4096x4096)
        if (img.width > 4096 || img.height > 4096) {
          resolve({ valid: false, error: t.imageTooLarge });
        } else {
          resolve({ valid: true });
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ valid: false, error: t.notValidImage });
      };

      img.src = url;
    });
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setTimeout(() => {
            checkAdminRole(session.user.id);
          }, 0);
        } else {
          setIsAdmin(false);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        checkAdminRole(session.user.id);
      } else {
        setLoading(false);
        navigate("/admin/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (selectedCategory && selectedSlug) {
      fetchImages();
    }
  }, [selectedCategory, selectedSlug]);

  const checkAdminRole = async (userId: string) => {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();

    if (data) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
    setLoading(false);
  };

  const fetchImages = async () => {
    if (!selectedCategory || !selectedSlug) return;
    
    const categoryValue = selectedCategory as "aluminum" | "iron" | "glass" | "marble" | "stainless-steel" | "wood";
    
    const { data, error } = await supabase
      .from("product_images")
      .select("*")
      .eq("category", categoryValue)
      .eq("product_slug", selectedSlug)
      .order("display_order", { ascending: true });

    if (data) {
      setImages(data);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    if (!selectedCategory || !selectedSlug) {
      toast({
        title: t.error,
        description: language === "ar" ? "يرجى اختيار الفئة والمنتج أولاً" : "Please select category and product first",
        variant: "destructive",
      });
      return;
    }

    const file = e.target.files[0];

    // Validate file before upload
    const validation = await validateImageFile(file);
    if (!validation.valid) {
      toast({
        title: t.error,
        description: validation.error,
        variant: "destructive",
      });
      // Reset the input
      e.target.value = '';
      return;
    }

    setUploading(true);
    
    // Get extension from MIME type for security
    const mimeToExt: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/webp': 'webp',
      'image/gif': 'gif',
    };
    const fileExt = mimeToExt[file.type] || 'jpg';
    const fileName = `${selectedCategory}/${selectedSlug}/${Date.now()}.${fileExt}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from("product-images")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("product-images")
        .getPublicUrl(fileName);

      const categoryValue = selectedCategory as "aluminum" | "iron" | "glass" | "marble" | "stainless-steel" | "wood";

      const { error: dbError } = await supabase.from("product_images").insert({
        category: categoryValue,
        product_slug: selectedSlug,
        image_url: urlData.publicUrl,
        title: imageTitle || null,
        display_order: images.length,
      });

      if (dbError) throw dbError;

      toast({ title: t.uploadSuccess });
      setImageTitle("");
      fetchImages();
    } catch (error) {
      toast({
        title: t.error,
        description: String(error),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (image: ProductImage) => {
    if (!confirm(t.deleteConfirm)) return;

    try {
      // Extract file path from URL
      const urlParts = image.image_url.split("/product-images/");
      if (urlParts[1]) {
        await supabase.storage.from("product-images").remove([urlParts[1]]);
      }

      const { error } = await supabase
        .from("product_images")
        .delete()
        .eq("id", image.id);

      if (error) throw error;

      toast({ title: t.deleteSuccess });
      fetchImages();
    } catch (error) {
      toast({
        title: t.error,
        description: String(error),
        variant: "destructive",
      });
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/auth");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    navigate("/admin/auth");
    return null;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <Card className="max-w-md mx-auto">
            <CardContent className="py-8">
              <p className="text-lg mb-4">{t.notAdmin}</p>
              <p className="text-muted-foreground mb-4">{user.email}</p>
              <Button onClick={handleLogout} variant="outline">
                <LogOut className="w-4 h-4 mr-2" />
                {t.logout}
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      <div className="container mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-kufi font-bold text-primary">{t.title}</h1>
          <Button onClick={handleLogout} variant="outline">
            <LogOut className="w-4 h-4 mr-2" />
            {t.logout}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upload Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                {t.uploadImage}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t.category}</Label>
                <Select value={selectedCategory} onValueChange={(value) => {
                  setSelectedCategory(value);
                  setSelectedSlug("");
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder={t.selectCategory} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(categories).map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {catNames[cat as keyof typeof catNames]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedCategory && (
                <div className="space-y-2">
                  <Label>{t.product}</Label>
                  <Select value={selectedSlug} onValueChange={setSelectedSlug}>
                    <SelectTrigger>
                      <SelectValue placeholder={t.selectProduct} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories[selectedCategory as keyof typeof categories]?.map((slug) => (
                        <SelectItem key={slug} value={slug}>
                          {slug}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>{t.imageTitle}</Label>
                <Input
                  value={imageTitle}
                  onChange={(e) => setImageTitle(e.target.value)}
                  placeholder={t.imageTitle}
                />
              </div>

              <div className="space-y-2">
                <Label>{t.selectFile}</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={uploading || !selectedCategory || !selectedSlug}
                />
              </div>

              {uploading && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  <span>{language === "ar" ? "جاري الرفع..." : "Uploading..."}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Images List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                {t.existingImages}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {images.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">{t.noImages}</p>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {images.map((image) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={image.image_url}
                        alt={image.title || "Product image"}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDelete(image)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {image.title && (
                        <p className="text-sm mt-1 truncate">{image.title}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminDashboard;
